import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useUpload } from '../contexts/UploadContext';
import KeepOriginalToggle from './KeepOriginalToggle';

// When the keep-original opt-in is ON, the picker advertises audio-only and
// the drop/select pipeline strips videos. Backend (audio-processor Lambda)
// can extract audio from video, but it cannot "keep the original" of a
// video as audio — keeping the original would store the MP4 alongside the
// transcoded MP3, which is not what the user is asking for. So when
// keep-original is on, video is disqualified at the entry point.
// Extensions FIRST, then MIME hints as a fallback. The HTML `accept`
// attribute is advisory — macOS Finder/Safari/Chrome honor extension lists
// more reliably (non-matching files gray out at the Finder level), and a
// trailing `audio/*` won't loosen that grip for the keep-original case.
// Audio extension allowlist mirrors the backend's audio-only allowlist
// (`lambda-upload-presigned` / audio-processor): wav, flac, aif, aiff, mp3,
// m4a, ogg, opus, alac. Any divergence here is a UX-vs-server lie.
const ACCEPT_AUDIO_ONLY = '.wav,.flac,.aif,.aiff,.mp3,.m4a,.ogg,.opus,.alac,audio/*';
const ACCEPT_AUDIO_AND_VIDEO_COLLAPSED = '.wav,.flac,.aif,.aiff,.mp3,.m4a,.ogg,.opus,.alac,.mp4,.mov,.webm,.m4v,audio/*,video/mp4,video/quicktime,video/webm';

// Audio detection — MIME first (browsers usually fill it for common types),
// extension fallback for the cases where the browser leaves type empty
// (some iOS pickers, some uncommon containers).
const VIDEO_EXTENSION_RE = /\.(mp4|mov|webm|m4v|mkv|avi)$/i;
const isVideoFile = (f: File): boolean => {
  if (f.type.startsWith('video/')) return true;
  if (f.type === '' && VIDEO_EXTENSION_RE.test(f.name)) return true;
  return false;
};

interface UploadZoneProps {
  onFileSelect: (files: File[]) => void;
  isUploading: boolean;
  isDragging: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  multiple?: boolean;
  collapsed?: boolean; // NEW: Compact mode
  isAuthenticated?: boolean; // NEW: Hide sign-in link for authenticated users
  compactTitle?: string; // Overrides the "Add More Files" headline in collapsed mode
  compactSubtitle?: string; // Overrides the helper text in collapsed mode
  compactSize?: 'sm' | 'md'; // Bar height in collapsed mode (default 'sm')
  inputId?: string; // Disambiguate the hidden <input id> when multiple zones share a page
}

const UploadZone: React.FC<UploadZoneProps> = ({
  onFileSelect,
  isUploading,
  isDragging,
  onDragOver,
  onDragLeave,
  onDrop,
  multiple = true,
  collapsed = false,
  isAuthenticated = false,
  compactTitle,
  compactSubtitle,
  compactSize = 'sm',
  inputId = 'file-upload',
}) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { isProUser, isBetaUser, isAuthenticated: viewerIsAuthed } = useAuth();
  const { keepOriginalNext } = useUpload();

  // Check if user has Pro access
  const isPro = isProUser() || isBetaUser();

  // Keep-Original row visibility — three states:
  // - Anonymous viewers: hidden. Showing a locked upsell to someone who
  //   hasn't signed up yet is noise; the account funnel comes first.
  // - Authenticated free/unpaid: rendered `disabled` — greyed with a Pro
  //   pill, clicking anywhere routes to /pro. Presence-as-upsell so free
  //   users learn the feature exists before they can benefit from it.
  // - Pro/Beta: rendered fully functional.
  // The AuthContext's isAuthenticated is aliased to viewerIsAuthed to
  // avoid shadowing the incoming `isAuthenticated` prop (which drives the
  // sign-in-link visibility for the upgrade CTA card below and is passed
  // by some but not all consumers — auth context is the trustworthy source).
  const showKeepOriginalRow = viewerIsAuthed;
  const keepOriginalDisabled = !isPro;

  // Local message state for the keep-original video skip. Lives in the
  // component, not the context — it's a UI side-effect of an entry-point
  // gesture and doesn't need to survive remounts. Each new
  // drop/select replaces the prior message; null hides it.
  const [skippedVideoNotice, setSkippedVideoNotice] = useState<{
    count: number;
    names: string[];
  } | null>(null);

  // Hide completely during upload (batch-only mode)
  if (isUploading) {
    return null;
  }

  const validateAudioFile = (file: File): boolean => {
    // iOS Fix: iOS may not always set file.type correctly, so also check file extension.
    // Video sources are accepted because the backend (audio-processor Lambda)
    // extracts the audio stream via ffmpeg — the stored asset is still mp3.
    const isAudioType = file.type.startsWith('audio/') || file.type === '';
    const isVideoType = file.type.startsWith('video/');
    const hasAudioExtension = /\.(mp3|wav|m4a|aac|flac|ogg|opus|wma|aiff)$/i.test(file.name);
    const hasVideoExtension = /\.(mp4|mov|webm|m4v|mkv)$/i.test(file.name);

    if ((isAudioType || isVideoType) && (hasAudioExtension || hasVideoExtension)) {
      return true;
    } else if (hasAudioExtension || hasVideoExtension) {
      // File has audio/video extension but type is wrong/missing (common on iOS)
      console.log('File has audio/video extension, accepting despite type:', file.type);
      return true;
    }

    console.error('Invalid file type:', file.type, file.name);
    return false;
  };

  // When keep-original is ON, partition videos out of the incoming set
  // before they reach the parent. Surface a count in the inline notice so
  // the user can see what was skipped and why. When keep-original is OFF,
  // behaviour is unchanged — videos pass through and the audio-processor
  // Lambda extracts the audio stream.
  const partitionForKeepOriginal = (files: File[]): {
    accepted: File[];
    skipped: File[];
  } => {
    if (!keepOriginalNext) {
      return { accepted: files, skipped: [] };
    }
    const accepted: File[] = [];
    const skipped: File[] = [];
    for (const f of files) {
      if (isVideoFile(f)) {
        skipped.push(f);
      } else {
        accepted.push(f);
      }
    }
    return { accepted, skipped };
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    const filesArray = Array.from(fileList);
    const { accepted, skipped } = partitionForKeepOriginal(filesArray);

    if (skipped.length > 0) {
      setSkippedVideoNotice({ count: skipped.length, names: skipped.map(f => f.name) });
    } else {
      setSkippedVideoNotice(null);
    }

    const validFiles = accepted.filter(validateAudioFile);

    if (validFiles.length > 0) {
      console.log(`${validFiles.length} file(s) selected:`, validFiles.map(f => ({
        name: f.name,
        type: f.type,
        size: f.size
      })));
      onFileSelect(validFiles);
    } else if (skipped.length === 0) {
      // Only log the "no valid audio" case when we didn't already account
      // for the input via the skip path — otherwise the user sees a real
      // explanation in the inline notice.
      console.error('No valid audio files selected');
    }
    // Reset the input so re-selecting the same file fires onChange again
    e.target.value = '';
  };

  // Wrap the parent's onDrop so we can peek at e.dataTransfer.files for the
  // keep-original gate BEFORE delegating. The parent re-filters with its own
  // audio-only check (idempotent), so passing the event straight through is
  // safe. When keep-original is OFF this is a transparent forward.
  const handleDropWithGate = (e: React.DragEvent) => {
    if (keepOriginalNext) {
      const dropped = Array.from(e.dataTransfer.files);
      const skipped = dropped.filter(isVideoFile);
      if (skipped.length > 0) {
        setSkippedVideoNotice({ count: skipped.length, names: skipped.map(f => f.name) });
      } else if (dropped.length > 0) {
        setSkippedVideoNotice(null);
      }
    } else {
      // OFF case: clear any stale notice from a prior ON-state drop so it
      // doesn't linger after the user unchecks the box and tries again.
      setSkippedVideoNotice(null);
    }
    onDrop(e);
  };

  const dismissSkipNotice = () => setSkippedVideoNotice(null);

  // The picker's accept attribute mirrors the keep-original state. Normal
  // mode was already audio-only; collapsed mode used to allow video. When
  // keep-original is ON, both modes lock to audio-only so the native picker
  // does the first-line filtering for us.
  const acceptForNormal = ACCEPT_AUDIO_ONLY;
  const acceptForCollapsed = keepOriginalNext
    ? ACCEPT_AUDIO_ONLY
    : ACCEPT_AUDIO_AND_VIDEO_COLLAPSED;

  // Inline "skipped N video files" notice. Rendered inside the dropzone so
  // it reads as a response to *this* gesture rather than a page-level toast
  // that fades out before the user finishes reading. Click-to-dismiss; also
  // self-clears on the next drop/select. stopPropagation on the wrapper so
  // tapping it doesn't trigger the picker on collapsed/normal zones.
  const skipNoticeBlock = skippedVideoNotice && (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.18 }}
        className="mt-3 px-3 py-2.5 rounded-lg border max-w-lg mx-auto text-left flex items-start gap-2"
        style={{
          backgroundColor: 'rgba(250, 200, 0, 0.08)',
          borderColor: 'rgba(250, 200, 0, 0.35)',
        }}
        onClick={(e) => {
          e.stopPropagation();
          dismissSkipNotice();
        }}
        role="status"
        aria-live="polite"
      >
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#facc15' }} />
        <div className="min-w-0">
          <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
            {skippedVideoNotice.count === 1
              ? 'Skipped 1 video file.'
              : `Skipped ${skippedVideoNotice.count} video files.`}
          </p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
            Uncheck Keep original to include them.
          </p>
        </div>
      </motion.div>
    </AnimatePresence>
  );

  // Collapsed mode: Compact bar
  if (collapsed) {
    const isMd = compactSize === 'md';
    const wrapperPad = isMd ? 'p-6 sm:p-7' : 'p-4';
    const iconBox = isMd ? 'w-14 h-14' : 'w-10 h-10';
    const iconSize = isMd ? 'w-7 h-7' : 'w-5 h-5';
    const titleSize = isMd ? 'text-base sm:text-lg' : '';
    const subtitleSize = isMd ? 'text-sm' : 'text-xs';
    const dragRing = isDragging ? 'border-soundwave-500 ring-1 ring-soundwave-500/40' : '';
    return (
      <motion.div
        layout
        className={`rounded-2xl border ${wrapperPad} cursor-pointer hover:border-soundwave-500/50 transition-colors ${dragRing}`}
        style={{
          backgroundColor: 'var(--background-surface)',
          borderColor: isDragging ? '#ff4800' : 'var(--border-color)',
        }}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={handleDropWithGate}
        onClick={() => document.getElementById(inputId)?.click()}
        whileHover={{ scale: 1.01 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
      >
        {/* Hidden file input */}
        <input
          type="file"
          id={inputId}
          className="hidden"
          accept={acceptForCollapsed}
          onChange={handleFileInputChange}
          disabled={isUploading}
          capture={undefined}
          multiple={multiple}
        />

        <div className={`flex items-center ${isMd ? 'gap-4' : 'gap-3'}`}>
          <div className={`${iconBox} rounded-lg bg-soundwave-500/10 flex items-center justify-center flex-shrink-0`}>
            <Upload className={`${iconSize} text-soundwave-500`} />
          </div>
          <div className="min-w-0">
            <p className={`font-medium ${titleSize}`} style={{ color: 'var(--text-primary)' }}>
              {compactTitle || 'Add More Files'}
            </p>
            <p className={subtitleSize} style={{ color: 'var(--text-tertiary)' }}>
              {compactSubtitle || 'Drag and drop or click to browse'}
            </p>
          </div>
        </div>

        {/* Keep-Original opt-in. Rendered inside the collapsed bar so the
            affordance follows the dropzone into the "add more files" state —
            the user can flip it between batches. Authed free users see it
            disabled with a Pro pill routing to /pro; anon users see nothing. */}
        {showKeepOriginalRow && (
          <KeepOriginalToggle density="compact" disabled={keepOriginalDisabled} />
        )}

        {/* Video-skipped notice (keep-original ON only). Sits under the
            toggle so cause sits next to effect. */}
        {skipNoticeBlock}
      </motion.div>
    );
  }

  // Normal mode: Large dropzone
  return (
    <motion.div
      layout
      className={`
        relative rounded-2xl p-1 transition-all duration-300 border-2
        ${isDragging
          ? 'border-soundwave-500 bg-soundwave-500/10'
          : 'border-transparent'
        }
      `}
      style={{
        backgroundColor: isDragging ? undefined : 'var(--background-surface)',
        borderColor: isDragging ? '#ff4800' : 'var(--border-color)',
        borderStyle: 'dashed'
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={handleDropWithGate}
      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
    >
      {/* File input - always present but hidden */}
      <input
        type="file"
        id={inputId}
        className="hidden"
        accept={acceptForNormal}
        onChange={handleFileInputChange}
        disabled={isUploading}
        capture={undefined}
        multiple={multiple}
      />

      <div className={`
        rounded-2xl p-10 transition-all duration-300
        ${isDragging ? 'scale-[1.02]' : 'scale-100'}
      `}>
        <div className="text-center">
          <motion.div
            animate={isDragging ? { scale: 1.1, rotate: 5 } : { scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <Upload
              className="w-16 h-16 mx-auto mb-5"
              style={{ color: isDragging ? '#ff4800' : 'var(--text-tertiary)' }}
              strokeWidth={1.5}
            />
          </motion.div>

          <h3 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            {multiple ? 'Drop your audio files here' : 'Drop your audio file here'}
          </h3>
          <p className="mb-5 text-lg" style={{ color: 'var(--text-secondary)' }}>
            {multiple ? 'or click to browse multiple files from your device' : 'or click to browse from your device'}
          </p>

          {/* Upgrade benefit - Made prominent (for authenticated free users) */}
          {isAuthenticated && !isPro && (
            <div className="mb-4 px-4 py-3 rounded-lg border max-w-lg mx-auto" style={{
              backgroundColor: 'var(--background-surface)',
              borderColor: 'var(--border-color)'
            }}>
              <p className="text-base font-medium">
                <button
                  onClick={() => navigate('/profile/plan')}
                  className="text-soundwave-500 hover:text-soundwave-400 transition-colors"
                >
                  Upgrade to Pro
                </button>
                <span style={{ color: 'var(--text-secondary)' }}> for </span>
                <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>permanent storage</span>
                <span style={{ color: 'var(--text-secondary)' }}> (14 days → forever), usage tracking, and more</span>
              </p>
            </div>
          )}

          {/* Public Upload Notice - Made more prominent */}
          <div className="mb-4 px-4 py-3 rounded-lg border-2 max-w-lg mx-auto" style={{
            backgroundColor: theme === 'dark' ? 'rgba(255, 200, 0, 0.15)' : 'rgba(255, 200, 0, 0.1)',
            borderColor: theme === 'dark' ? 'rgba(255, 200, 0, 0.5)' : 'rgba(255, 200, 0, 0.6)'
          }}>
            <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
              Files are accessible to anyone with the link
            </p>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              By uploading, you agree to our{' '}
              <button onClick={() => navigate('/terms')} className="text-soundwave-500 hover:text-soundwave-400 underline cursor-pointer">
                Terms
              </button>
              {' '}and{' '}
              <button onClick={() => navigate('/privacy')} className="text-soundwave-500 hover:text-soundwave-400 underline cursor-pointer">
                Privacy Policy
              </button>
            </p>
          </div>

          {/* Keep-Original opt-in. Last block before the primary action so
              the user reads "files are public → keep original? → Choose
              Files" as one continuous pre-flight check. Authed free users
              see it disabled with a Pro pill routing to /pro; anon users
              see nothing. */}
          {showKeepOriginalRow && (
            <div className="mb-4">
              <KeepOriginalToggle density="normal" disabled={keepOriginalDisabled} />
            </div>
          )}

          {/* Video-skipped notice (keep-original ON only). Placed directly
              under the toggle and above the primary action so the user sees
              the explanation in the same eyeline as "Choose Files". */}
          {skipNoticeBlock && <div className="mb-4">{skipNoticeBlock}</div>}

          <label htmlFor={inputId}>
            <motion.div
              className="inline-block bg-soundwave-500 hover:bg-soundwave-600 text-white font-semibold px-10 py-3.5 rounded-xl cursor-pointer shadow-lg transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {multiple ? 'Choose Files' : 'Choose File'}
            </motion.div>
          </label>
        </div>
      </div>
    </motion.div>
  );
};

export default UploadZone;
